import PropTypes from 'prop-types';
import React, { useContext, useMemo, useState } from 'react';

const ProgressContext = React.createContext();

/**
 * Hook para acessar o contexto de progresso/loading
 * @returns {{
 *   status: {type: string, text: string}|null,
 *   setStatus: Function,
 *   headerContent: React.ReactNode|null,
 *   setHeaderContent: Function
 * }}
 */
export const useProgress = () => useContext(ProgressContext);

/**
 * Provider de contexto de progresso/loading
 *
 * Status Structure:
 * - Loading: { type: "loading", text: "Custom loading message" }
 * - Idle/None: null
 *
 * Header Content:
 * - Permite que componentes filhos (ex.: `ProcessDispatcher`) registrem
 *   um nó React (tipicamente uma `<img/>` da logo do cliente B2B obtida
 *   via `uiConfig.logo`) para ser renderizado pelo `DynamicWrapper` como
 *   `headerContent`. Como o `DynamicWrapper` vive somente no host e os
 *   dados (`uiConfig`) só ficam disponíveis depois do fetch do
 *   `ProcessDispatcher` — que está montado dentro do próprio wrapper —
 *   esse contexto é a ponte que permite ao filho empurrar conteúdo para
 *   o pai sem prop drilling reverso.
 *
 * @example
 * import { createLoadingStatus } from "../constants/status"
 *
 * setStatus(createLoadingStatus("Processando..."))
 * setStatus(null) // Para limpar
 *
 * setHeaderContent(<img src={uiConfig.logo} alt={uiConfig.companyName} />)
 * setHeaderContent(null) // Para limpar
 */
const ProgressProvider = ({ children }) => {
  /**
   * @type {[{type: string, text: string}|null, Function]}
   */
  const [status, setStatus] = useState();
  /**
   * @type {[React.ReactNode|null, Function]}
   */
  const [headerContent, setHeaderContent] = useState(null);

  const value = useMemo(
    () => ({
      status,
      setStatus,
      headerContent,
      setHeaderContent,
    }),
    [status, setStatus, headerContent, setHeaderContent],
  );

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};

ProgressProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ProgressProvider;
